import React from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import classNames from 'classnames'
import moment from 'moment'

import {
  fetchUpdateInNotificationUsage,
  fetchQuestionUsage,
  FETCH_UPDATE_IN_NOTIFICATION_USAGE,
  searchQuestionUpdate,
  SEARCH_FOR_UPDATES,
} from 'mednet-cns/src/reducers/questionUpdate'
import { getRequest } from 'mednet-cns/src/api/v1'
import { makeRequestName } from 'mednet-cns/src/reducers/request'

import { StarLoader } from 'pharmacy/src/misc/loaders/starLoader'
import { TabButton } from 'pharmacy/src/navigation/tabs'
import { DateRangePicker } from 'pharmacy/src/input/datePicker'
import TextInput from 'pharmacy/src/input/textInput/textInput'
import { TagSearch } from 'pharmacy/src/display/tagSearch'

import { Button } from 'pharmacy/src/input/button'

import { ErrorPage } from 'pages/error'
import UpdateCard from 'components/updateCard/updateCard'
import {
  LastUsedLegend,
  getDigestUsageState,
  USAGE_STATES,
} from 'components/lastUsedInfo'

import Tabs from './searchQuestionUpdatesListTab'

import css from './searchQuestionUpdates.scss'

const UPDATE_TYPES = {
  ANSWERS: 'answers',
  QUESTIONS: 'questions',
  POLLS: 'polls',
}

const UPDATE_PAGE_SIZE = 25

class SearchQuestionUpdatesPage extends React.Component {
  state = {
    types: _.values(UPDATE_TYPES),
    from: moment().subtract(5, 'days').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
    text: '',
    topics: [],
    author: 0,
    advanced: false,
    hidePreviouslySentUpdates: false,
    page: 1, // page is 1-indexed on backend
  }

  componentDidMount() {
    this.search()
  }

  updateDateRange = ({ from, to }) => {
    const runSearch = from && to
    this.setState({ from, to, page: 1 }, () => runSearch && this.search())
  }

  updateState = (name) => (value) => {
    this.setState({ page: 1, [name]: value }, this.search)
  }

  search = _.debounce(() => {
    const { searchQuestionUpdate } = this.props
    const { types, from, to, text, author, page } = this.state
    const params = {
      types,
      page,
      pageSize: UPDATE_PAGE_SIZE,
    }

    if (from && to) {
      params.from = from
      params.to = to
    }

    if (text) {
      params.text = text
    }

    if (author && author.userId) {
      params.author = author.userId
    }

    searchQuestionUpdate(params, this.fetchUpdateInNotificationUsage)
  }, 1000)

  getTabs = () => {
    let { answers, questions, polls } = this.props
    const {
      updatesInThisDigest,
      notifications,
      isLoadingUpdatesInfo,
      isLoading,
    } = this.props
    const { hidePreviouslySentUpdates } = this.state
    const isLoadingAnything = isLoading || isLoadingUpdatesInfo
    const differenceBy = (item) => item.questionUpdateId
    // The "hide previously sent" toggle drops only the gray (sent and current)
    // cards. Answers updated since they were sent (purple) have new, undelivered
    // content worth re-sending, so they stay visible; never-sent (pink) stay too.
    // Uses the same classifier as the chip so the two can't disagree.
    const isNotPreviouslySent = (update) =>
      getDigestUsageState(
        _.get(notifications, `${update.questionUpdateId}`),
        update.answer?.updated
      ) !== USAGE_STATES.SENT

    // Raw per-tab counts as fetched from the backend, before any client-side
    // filtering — shown as the tab's fetched total.
    const fetchedCounts = {
      answer: answers.length,
      poll: polls.length,
      question: questions.length,
    }

    // Drop updates already added to the digest being built; the "Add more" panel
    // shouldn't offer content that's already in.
    answers = _.differenceBy(answers, updatesInThisDigest, differenceBy)
    questions = _.differenceBy(questions, updatesInThisDigest, differenceBy)
    polls = _.differenceBy(polls, updatesInThisDigest, differenceBy)

    // Counts after removing updates already in this digest but before the "hide
    // previously sent" toggle — i.e. the updates that toggle can act on. The
    // "looks short" note compares against these so it reflects only what that
    // toggle hid, not the already-in-this-digest removal.
    const notInThisDigestCounts = {
      answer: answers.length,
      poll: polls.length,
      question: questions.length,
    }

    if (hidePreviouslySentUpdates) {
      answers = answers.filter(isNotPreviouslySent)
      questions = questions.filter(isNotPreviouslySent)
      polls = polls.filter(isNotPreviouslySent)
    }

    return [
      {
        header: (
          <TabButton
            className={css.tabHeader}
            isLoading={isLoadingAnything}
            title={
              isLoadingAnything ? 'Answers' : `Answers (${answers.length})`
            }
          />
        ),
        component: this.renderTabContent(
          answers,
          'answer',
          fetchedCounts.answer,
          notInThisDigestCounts.answer,
          hidePreviouslySentUpdates
        ),
      },
      {
        header: (
          <TabButton
            className={css.tabHeader}
            isLoading={isLoadingAnything}
            title={isLoadingAnything ? 'Polls' : `Polls (${polls.length})`}
          />
        ),
        component: this.renderTabContent(
          polls,
          'poll',
          fetchedCounts.poll,
          notInThisDigestCounts.poll,
          hidePreviouslySentUpdates
        ),
      },
      {
        header: (
          <TabButton
            className={css.tabHeader}
            isLoading={isLoadingAnything}
            title={
              isLoadingAnything
                ? 'Questions'
                : `Questions (${questions.length})`
            }
          />
        ),
        component: this.renderTabContent(
          questions,
          'question',
          fetchedCounts.question,
          notInThisDigestCounts.question,
          hidePreviouslySentUpdates
        ),
      },
    ]
  }

  toggleUsedUpdatesVisiblity = () => {
    this.setState((state) => ({
      hidePreviouslySentUpdates: !state.hidePreviouslySentUpdates,
    }))
  }

  fetchUpdateInNotificationUsage = (response) => {
    const { fetchUpdateInNotificationUsage, fetchQuestionUsage } = this.props
    const updates = _.concat(
      response.answers,
      response.questions,
      response.polls
    )
    const updateIds = updates.map((update) => update.questionUpdateId)

    // An empty search response would request usage for no updates, which marks
    // the fetch loaded with no data and hides the "last used" label on cards
    // whose usage isn't already cached.
    if (!updateIds.length) {
      return
    }

    fetchUpdateInNotificationUsage(updateIds)

    // Batch the "another item from this question" usage for every question on the
    // page in one request, instead of each card's usage line fetching its own — the
    // same one-request-per-page pattern the update-usage uses.
    const questionIds = _.uniq(updates.map((update) => update.questionId))
    fetchQuestionUsage(questionIds)
  }

  onNextPage = () => {
    this.setState((prev) => ({ page: prev.page + 1 }), this.search)
  }

  onPrevPage = () => {
    this.setState((prev) => ({ page: prev.page - 1 }), this.search)
  }

  renderPaginationButtons = () => (
    <div className={css.pagination}>
      <Button
        icon="chevron-left"
        size="small"
        shape="circle"
        isDisabled={this.state.page <= 1 || this.props.isLoading}
        onClick={this.onPrevPage}
      />
      <span>Page {this.state.page}</span>
      <Button
        icon="chevron-right"
        size="small"
        shape="circle"
        isDisabled={this.props.isLoading}
        onClick={this.onNextPage}
      />
    </div>
  )

  renderFilters() {
    const { from, to, text, author } = this.state
    return (
      <div>
        <div className={css.row}>
          <TextInput
            value={text}
            onChange={this.updateState('text')}
            className={classNames(css.text)}
            placeholder="Search text in question, answer, polls..."
          ></TextInput>
        </div>
        <div className={classNames(css.row)}>
          <TagSearch
            index="user"
            name="author"
            placeholder="Search for author..."
            className={classNames(css.author)}
            onChange={this.updateState('author')}
            value={author}
            isMulti={false}
            hitsPerPage={10}
            openMenuOnClick={false}
            isClearable
          />
          <DateRangePicker
            fromProps={{ defaultValue: from }}
            toProps={{ defaultValue: to, justifyOverlay: 'right' }}
            toDisabledDays={(from) => ({ before: from, after: new Date() })}
            onChange={this.updateDateRange}
            className={css.dateRangePicker}
          />
        </div>
      </div>
    )
  }

  renderTabContent(
    updates,
    type,
    fetchedCount,
    notInThisDigestCount,
    hidePreviouslySentUpdates
  ) {
    const {
      specialtyId,
      specialtyTitle,
      onAddUpdate,
      updateIsLoading,
      isLoading,
    } = this.props

    if (isLoading) {
      return <StarLoader />
    }

    // The backend paginates before the client-side filters run, so a full page
    // can display only a few cards. Show the note only when the "hide previously
    // sent" toggle is on and actually hid some — notInThisDigestCount is measured
    // after removing updates already in this digest, so updates.length <
    // notInThisDigestCount means the toggle is what thinned the page. Gate on a
    // full backend page (fetchedCount) so we don't promise more when this is
    // already the last page.
    const showHiddenByFilterNote =
      hidePreviouslySentUpdates &&
      fetchedCount >= UPDATE_PAGE_SIZE &&
      updates.length < notInThisDigestCount

    return (
      <div className={css.tab}>
        {this.renderPaginationButtons()}
        {updates.map((update) => (
          <UpdateCard
            key={`${type}.${update.questionUpdateId}`}
            type={type}
            update={update}
            className={css.card}
            onAddUpdate={onAddUpdate}
            updateIsLoading={updateIsLoading}
            specialtyId={specialtyId}
            specialtyTitle={specialtyTitle}
          ></UpdateCard>
        ))}
        {!showHiddenByFilterNote ? null : (
          <div className={css.filterNote}>
            “Hide used updates” is hiding some updates on this page, so it may
            look short even when more updates exist on later pages.
          </div>
        )}
      </div>
    )
  }

  renderUpdatesList() {
    const { isError, errorStatus, isUsageLoading } = this.props

    if (isError) {
      return <ErrorPage errorStatus={errorStatus}></ErrorPage>
    }

    return (
      <Tabs
        headerProps={{
          toggleUsedUpdatesVisiblity: this.toggleUsedUpdatesVisiblity,
          isLoading: isUsageLoading,
        }}
        tabs={this.getTabs()}
      />
    )
  }

  render() {
    return (
      <div className={css.main}>
        <div className={css.legend}>
          <LastUsedLegend />
        </div>
        <div>{this.renderFilters()}</div>
        <hr className={css.divider} />
        <div>{this.renderUpdatesList()}</div>
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { specialtyId } = ownProps
  const { answers, questions, polls } = state.questionUpdate.search[
    specialtyId
  ] || {
    answers: [],
    questions: [],
    polls: [],
  }
  const notifications = state.questionUpdate.notifications[specialtyId] || {}

  const searchUpdatesRequest = getRequest(
    state,
    makeRequestName(SEARCH_FOR_UPDATES, specialtyId)
  )

  const lastUsedUpdateRequest =
    _.get(
      state.request.requests,
      makeRequestName(FETCH_UPDATE_IN_NOTIFICATION_USAGE)
    ) || {}

  return {
    answers,
    questions,
    polls,
    notifications,
    isLoading: searchUpdatesRequest.isLoading,
    errorStatus: searchUpdatesRequest.errorStatus,
    isError: searchUpdatesRequest.errorStatus,
    isLoadingUpdatesInfo: lastUsedUpdateRequest.isLoading,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  searchQuestionUpdate: (params, callback) =>
    dispatch(searchQuestionUpdate(ownProps.specialtyId, params, callback)),
  fetchUpdateInNotificationUsage: (updateIds) =>
    dispatch(
      fetchUpdateInNotificationUsage(ownProps.specialtyId, updateIds, {
        digestIds: ownProps.todayDigestIds, // A day can have muliple digests because there can be cohorts
        newsletterIds: [ownProps.newsletterId],
      })
    ),
  fetchQuestionUsage: (questionIds) =>
    dispatch(
      fetchQuestionUsage(ownProps.specialtyId, questionIds, {
        // Same exclusion as the item chip above: don't count the digest/newsletter
        // being edited as "previously sent" on the "another item" line.
        digestIds: ownProps.todayDigestIds,
        newsletterIds: [ownProps.newsletterId],
      })
    ),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchQuestionUpdatesPage)
